import { type ReactElement } from 'react';

import { INLINE_LINK, CtaLink, Panel } from '@/components/bedrock';
import { Container, PageHero } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import { CONTACT_ADDRESSES, SINGLE_INBOX } from '@/features/contact/contact-addresses';
import { ContactForm } from '@/features/contact/ContactForm';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

/**
 * `/contact` — the B25 lead-capture page (operator decision 2026-06-11:
 * email form + published aliases; no booking link).
 *
 * Three sections:
 *   1. The form (lane-C `POST /v1/contact` contract, graceful mailto
 *      fallback — see ContactForm).
 *   2. The published addresses for form-haters — `sales@syncanix.com` for
 *      sales/billing (operator directive 2026-07-16) and `admin@syncanix.com`,
 *      the general monitored inbox for support/security/press (see
 *      contact-addresses.ts).
 *   3. The design-partner pipeline (the formerly-orphaned `customers.*`
 *      block, B25(4) wire-or-delete — wired here, where its CTA lands on the
 *      form above it). Its discount figure is aligned to the verified
 *      negotiation-playbook give (20% year-1 for logo + quote + 2 reference
 *      calls); the unverifiable 30% draft figure was corrected ×6 locales.
 */

export function Contact(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('contact.seo.title'),
    description: t('contact.seo.description'),
    path: '/contact',
  });

  return (
    <main id="main" tabIndex={-1} className="min-h-screen bg-[color:var(--bd-page)]">
      <PageHero title={t('contact.heading')} description={t('contact.intro')} />

      {/* Two columns, because the form alone was a 720px strip down the middle
          of a 1440px page with the whole right half empty — and because the two
          things a visitor wants beside a contact form (where else can I reach
          you, how fast will you reply) were 900px BELOW it, where somebody
          about to type is not looking. */}
      <Container width="default" className="py-12 sm:py-16">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)] lg:gap-14">
          <Panel className="p-6 sm:p-8">
            <ContactForm />
          </Panel>

          <div className="flex flex-col gap-4 lg:sticky lg:top-[calc(var(--bd-nav-h)+2rem)] lg:self-start">
            <Panel as="section" aria-labelledby="contact-addresses" className="p-6">
              <h2 id="contact-addresses" className="mb-2 text-base font-semibold">
                {t('contact.addresses.heading')}
              </h2>
              <p className="mb-4 text-sm text-[color:var(--bd-muted)]">
                {t('contact.addresses.intro')}
              </p>
              {/* Both published addresses, scoped truthfully: sales enquiries have
              their own address (sales@, which the Sales-topic form also routes
              to); admin@ stays the general monitored inbox for
              support/security/press. The old single-line copy claimed admin@
              covered sales too — a leftover from before the 2026-07-16 sales@
              split. */}
              <p className="text-sm">
                <a className={INLINE_LINK} href={`mailto:${CONTACT_ADDRESSES.sales}`}>
                  {CONTACT_ADDRESSES.sales}
                </a>{' '}
                <span className="text-[color:var(--bd-muted)]">
                  · {t('contact.addresses.sales')}
                </span>
              </p>
              <p className="mt-2 text-sm">
                <a className={INLINE_LINK} href={`mailto:${SINGLE_INBOX}`}>
                  {SINGLE_INBOX}
                </a>{' '}
                <span className="text-[color:var(--bd-muted)]">· {t('contact.addresses.all')}</span>
              </p>
            </Panel>

            <Panel accent as="section" aria-labelledby="design-partners" className="p-6">
              <h2 id="design-partners" className="mb-2 text-base font-semibold">
                {t('customers.heading')}
              </h2>
              <p className="mb-3 text-sm text-[color:var(--bd-muted)]">{t('customers.intro')}</p>
              <h3 className="mb-1 text-sm font-semibold">{t('customers.pipelineLabel')}</h3>
              <p className="mb-4 text-sm text-[color:var(--bd-muted)]">
                {t('customers.pipelineNote')}
              </p>
              <CtaLink
                variant="secondary"
                size="sm"
                href={`mailto:${CONTACT_ADDRESSES.sales}?subject=${encodeURIComponent('Design partner')}`}
              >
                {t('customers.designPartnerCta')}
              </CtaLink>
            </Panel>
          </div>
        </div>
      </Container>
    </main>
  );
}
